import React, { useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import moment from "moment";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { DataGrid, GridColDef, GridFilterItem, GridSortModel, GridToolbar } from "@mui/x-data-grid";
import { Alert, Button, Collapse, IconButton } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import CardContent from "@mui/material/CardContent";
import ExportButton from "./exportButton";
import PageHeader from "&styled/page-header/pageHeader";
import { RootState } from "&store/store";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { loanRequestsActions } from "./loanRequests.slice";
import { getCurrentUser } from "&config/getCurrentUser";
import SearchIcon from "&assets/icons/search";
import AvatarIcon from "&assets/images/avatar.png";
import CheckIcon from "&assets/icons/check";
import CrossIcon from "&assets/icons/x";
import UploadIcon from "&assets/icons/upload";
import ConfirmationModal from "./confirmationModal";
import UploadModal from "./uploadModal";
import { CheckMimeType } from "&styled/file-upload/check-mime-type";
import { OutlinedButton, SubmitButton } from "&styled/button/button.component";
import DatePickerComponent from "&styled/form/date-picker";
import { colorPalette } from "&config/colors";
import RefreshIcon from "@mui/icons-material/Refresh";
import { errorAlert, infoAlert, successAlert } from "&config/swalGenerator";
import LogsModal from "./logsModal";
import InfoIcon from "&assets/icons/info";
import DeleteIcon from "&assets/icons/delete";
import { useFormik } from "formik";
import { TextInput } from "&styled/textField/textField.component";
import * as Yup from "yup";
import banksList from "../../utils/banks";
import SelectComponent from "&styled/form/select";
import ExportIcon from "&assets/icons/export";
import momentTimezone from "moment-timezone";
import { CSVLink } from "react-csv";

type ReduxProps = ConnectedProps<typeof connector>;

interface State {
  query: string;
  status: string;
  from: string;
  to: string;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "34px",
  p: 2,
};

const validationSchema = Yup.object().shape({
  amount: Yup.number().min(1, "Amount must be greater than 0").required("Provide valid amount"),
  cnic: Yup.string()
    .required("Provide valid CNIC")
    .matches(/^\d{13}$/, "CNIC must be 13 digits"),
  certName: Yup.string()
    .min(2, "Provide valid surname and personal name")
    .max(50, "Provide valid surname and personal name")
    .required("Provide valid surname and personal name"),
  payoutType: Yup.string()
    .oneOf(["BANK", "WALLET", "CNIC"], "Payout Type should be one of BANK | WALLET | CNIC")
    .required("Payout Type is required"),
  bankName: Yup.string().required("Provide valid bank name"),
  telco: Yup.string()
    .trim()
    .required("Telco is required")
    .oneOf(["jazz", "telenor", "ufone", "zong"], "Telco must be one of: jazz | telenor | ufone | zong"),
  msisdn: Yup.string()
    .matches(/^03\d{9}$/, "MSISDN must be 11 digits and start with 03")
    .required("Provide valid MSISDN"),
  bankNo: Yup.string()
    .min(2, "Provide valid bank number")
    .max(30, "Provide valid bank number")
    .required("Provide valid bank number"),
  billingDate: Yup.string().required("Billing date is required"),
  channel: Yup.string().required("Channel is required").oneOf(["Easypaisa"]),
});

const LoanRequestsComponent = (props: ReduxProps) => {
  const {
    state,
    getLoanRequests,
    searchLoanRequests,
    token,
    changeStatus,
    fileParsed,
    uploadProgress,
    deleteFile,
    resetUploadProgress,
    processAutoJobs,
    processEasypaisaAutoJobs,
    processSwitchAutoJobs,
    processSwitchTitleFetchAutoJobs,
    getJobStatus,
    refreshLoanRequest,
    deleteLoanRequest,
    addAdminLoanRequests,
    getLoanRequestsExport,
  } = props;

  const { data, autoJobStatus, epAutoJobStatus, switchAutoJobStatus, switchTitleFetchAutoJobStatus } = state;
  const user: any = getCurrentUser(token);
  const ref = useRef(null);
  const { userTypeId } = user;
  const [open, setOpen] = React.useState(false);
  const [loanRec, setLoanRec] = useState<any>({});
  const [fileUploadBtnDisabled, setFileUploadBtnDisabled] = useState(false);
  const [fileUploadSummary, setFileUploadSummary] = useState({});
  const [pageSize, setPageSize] = React.useState(25);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [file, setFile] = useState<any>("");
  const [recId, setRecId] = useState("");
  const [logId, setLogId] = useState("");
  const [recMerchant, setRecMerchant] = useState("");
  const [recAmount, setRecAmount] = useState("");
  const [recStatus, setRecStatus] = useState("");
  const [uploadView, setUploadView] = useState("fileUpload");
  const [error, setError] = useState("");
  const [loanRequestModal, setLoanRequestModal] = useState<null | "step-1" | "step-2">(null);
  const [addLoanRequestResponse, setAddLoanRequestResponse] = useState<any>(null);
  const [exportData, setExportData] = useState([]);

  const [search, setSearch] = useState<{
    sorting: GridSortModel;
    filtering: GridFilterItem[];
    sortObj: { field: string; sortBy: "asc" | "desc" };
    page: number;
    limit: number;
    query: string;
    from: Date;
    to: Date;
    status: string;
    source: string;
    run: boolean;
  }>({
    query: "",
    status: "All",
    source: "all",
    from: new Date(),
    to: new Date(),
    sorting: [],
    filtering: [],
    page: 0,
    limit: 10,
    run: true,
    sortObj: {
      field: "createdDate",
      sortBy: "asc",
    } as any,
  });

  let pkFormatter = new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
  });

  const handleOpenConfirmationModal = (row, status) => {
    setRecId(row._id);
    setRecAmount(row.amount);
    setRecMerchant(row.userTypeId);
    setRecStatus(status);
    setConfirmationModalOpen(true);
  };
  const handleCloseConfirmationModal = () => {
    setRecId("");
    setRecAmount("");
    setRecMerchant("");
    setRecStatus("");
    setConfirmationModalOpen(false);
  };
  const handleOpenLogsModal = (row) => {
    setLogId(row.loanId);
  };
  const handleCloseLogsModal = () => {
    setLogId("");
  };
  const handleFileUploadModalClose = () => {
    setError("");
    setFileUploadSummary({});
    setUploadView("fileUpload");
    setFile("");
    resetUploadProgress(0);
    setFileUploadBtnDisabled(false);
    setFileUploadModalOpen(false);
  };
  const handleClose = () => setOpen(false);

  const handleChangeStatus = async () => {
    if (["Approved", "Rejected"].includes(recStatus)) {
      const params = {
        id: recId,
        status: recStatus,
        amount: recAmount,
        merchant: recMerchant,
      };
      await changeStatus(params);
    }
    if (recStatus === "Refresh") {
      const data = await refreshLoanRequest({ _id: recId });
      if (data?.payload?.status === "success") {
        successAlert("Info", data?.payload.message);
      } else if (data?.payload?.status === "failed") {
        infoAlert("Error", data?.payload.message);
      } else {
        errorAlert();
      }
    }
    if (recStatus === "Delete") {
      const data = await deleteLoanRequest({ _id: recId });
      if (data?.payload?.status === "success") {
        successAlert("Info", data?.payload.message);
      } else if (data?.payload?.status === "failed") {
        infoAlert("Error", data?.payload.message);
      } else {
        errorAlert();
      }
    }
    setConfirmationModalOpen(false);
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!CheckMimeType(file)) {
      return toast.error("Only xlsx & xls files supported", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    setFile(file);
  };
  const handleFileUpload = async () => {
    if (!file) {
      return toast.error("Please select file", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    setFileUploadBtnDisabled(true);
    try {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const acceptableCols = ["Loan ID", "Amount", "Status"];
        const cols = Object.keys(jsonData[0]);
        const missingCol = cols.filter((c) => !acceptableCols.includes(c));
        if (missingCol.length > 0) {
          setUploadView("error");
          return setError("Invalid Columns");
        }

        const loanIdsArray = jsonData.map((r) => r["Loan ID"]);
        if (new Set(loanIdsArray).size !== loanIdsArray.length) {
          setUploadView("error");
          return setError("Duplicate Entries");
        }

        const summary: any = {
          Approved: 0,
          Rejected: 0,
          Pending: 0,
          amount: 0,
          total: 0,
          invalidStatus: [],
          size: file.size,
          originalName: file.name,
        };

        const validStatus = ["Approved", "Rejected"];
        jsonData.forEach((object) => {
          if (!validStatus.includes(object.Status)) {
            summary.Pending++;
            summary.invalidStatus.push({
              loanId: object["Loan ID"],
              status: object.Status,
            });
          }
          summary[object.Status]++;
          summary.total++;
          summary.amount += object.Status === "Approved" ? parseInt(object.Amount, 10) : 0;
        });

        setFileUploadSummary(summary);
        setUploadView("fileInfo");
      };
      reader.readAsArrayBuffer(file);
    } catch (e) {
      console.log(e);
    }

    setFileUploadBtnDisabled(false);
  };

  const columns: GridColDef[] = [
    {
      field: "loanId",
      headerName: "Payout ID",
      renderCell: ({ row }) => (
        <Box sx={{ color: "#6631F7", cursor: "pointer" }} onClick={() => handleModalOpen(row._id)}>
          {row.loanId}
        </Box>
      ),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "merchantName",
      headerName: "Merchant",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "certName",
      headerName: "Name",
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "contact",
      headerName: "Contact",
      flex: 1,
      renderCell: ({ row }) => renderContact(row),
      headerAlign: "left",
      align: "left",
    },
    {
      field: "createdDate",
      headerName: "Request Date",
      renderCell: ({ row }) => {
        return momentTimezone(row.createdDate).tz("Asia/Karachi").format("DD MMM YYYY HH:mm A");
      },
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "source",
      headerName: "Source",
      renderCell: ({ row }) => {
        return row?.source?.toUpperCase();
      },
      flex: 1,
      headerAlign: "left",
      align: "left",
    },

    {
      field: "amount",
      headerName: "Amount",
      renderCell: ({ row }) => pkFormatter.format(row.amount),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "status",
      headerName: "STATUS",
      renderCell: ({ row }) => renderStatus(row.status),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "actions",
      headerName: "",
      renderCell: ({ row }) => renderButtons(row),
      width: 150,
      headerAlign: "left",
      align: "left",
    },
  ];

  useEffect(() => {
    (async () => {
      await Promise.all([getJobStatus()]);
    })();
  }, [getJobStatus]);

  // handler
  const handleModalOpen = (id) => {
    const loan = data?.data?.find((l) => l._id === id);
    setLoanRec(loan);
    setOpen(true);
  };

  // renderer
  const renderContact = (row) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            color: "#111111",
            font: "normal normal normal 12px/18px Poppins",
          }}
        >
          {row.beneficiaryEmail}
        </Box>
        <Box
          sx={{
            color: "#838383",
            font: "normal normal normal 9px/13px Poppins",
          }}
        >
          {row.msisdn}
        </Box>
      </Box>
    );
  };
  const renderStatus = (status) => {
    return (
      <Box
        sx={{
          backgroundColor: status === "Pending" ? "#ffefbd" : status === "Approved" ? "#D6FFD9" : "#FFE4E4",
          color: status === "Pending" ? "#9b7400" : status === "Approved" ? "#0FA01A" : "#FF1D1D",
          width: "62px",
          textAlign: "center",
          borderRadius: "16px",
          padding: "5px",
          font: "normal normal normal 10px/16px Poppins",
        }}
      >
        {status}
      </Box>
    );
  };

  const handleChange = (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch({ ...search, [prop]: event.target.value });
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setSearch((prevState) => ({
      ...prevState,
      status: event.target.value as string,
    }));
  };

  const handleDateChange = (val, type) => {
    setSearch((prevState) => ({ ...prevState, [type]: val }));
  };
  const renderLogs = (row) => {
    return (
      <Box
        sx={{
          width: "30px",
          height: "30px",
          background: "#E6FFEF 0% 0% no-repeat padding-box",
          border: "2px solid #0DB94C",
          borderRadius: "25px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleOpenLogsModal(row)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <InfoIcon />
        </Box>
      </Box>
    );
  };
  const renderReject = (row) => {
    return (
      <Box
        sx={{
          width: "30px",
          height: "30px",
          background: "#FFECEC 0% 0% no-repeat padding-box",
          border: "2px solid #E53030",
          borderRadius: "25px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleOpenConfirmationModal(row, "Rejected")}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CrossIcon />
        </Box>
      </Box>
    );
  };
  const renderApprove = (row) => {
    return (
      <Box
        sx={{
          width: "30px",
          height: "30px",
          background: "#FFECEC 0% 0% no-repeat padding-box",
          border: "2px solid #E53030",
          borderRadius: "25px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleOpenConfirmationModal(row, "Approved")}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CheckIcon />
        </Box>
      </Box>
    );
  };
  const renderInquire = (row) => {
    return (
      <Box
        sx={{
          width: "30px",
          height: "30px",
          background: "#E6FFEF 0% 0% no-repeat padding-box",
          border: "2px solid #0DB94C",
          borderRadius: "25px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleOpenConfirmationModal(row, "Refresh")}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <RefreshIcon />
        </Box>
      </Box>
    );
  };

  const renderDelete = (row) => {
    return (
      <Box
        sx={{
          width: "30px",
          height: "30px",
          background: "#FFECEC 0% 0% no-repeat padding-box",
          border: "2px solid #E53030",
          borderRadius: "25px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={() => handleOpenConfirmationModal(row, "Delete")}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <DeleteIcon />
        </Box>
      </Box>
    );
  };
  const isWalee = userTypeId === "Walee";
  const loanPerms = user?.role?.permissions?.LoanRequest;

  const canApprove = isWalee || loanPerms?.approvedRequest === true;
  const canReject = isWalee || loanPerms?.rejectRequest === true;
  const canDelete = isWalee || loanPerms?.deleteRequest === true;
  const canStartStopEpJob = isWalee || loanPerms?.startStopEpJob === true;
  const canStartStopJcJob = isWalee || loanPerms?.startStopJcJob === true;
  const canStartStopSwitchJob = isWalee || loanPerms?.startStopSwitchJob === true;

  const renderButtons = (row) => {
    const hasAnyAction = canApprove || canReject || canDelete;
    let isAbleToDelete = canDelete ?? false;
    if (row?.status !== "Pending" && !(canApprove && canReject)) {
      isAbleToDelete = false;
    }
    return (
      <>
        <Box mr={1}>{renderLogs(row)}</Box>
        {["Pending", "Approved", "Rejected"]?.includes(row.status) && hasAnyAction && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {isAbleToDelete && renderDelete(row)}
            {canReject && renderReject(row)}
            {canApprove && renderApprove(row)}
          </Box>
        )}
      </>
    );
  };

  const handleFileRec = () => {
    window.location.href = "/file-records";
  };

  const renderGhostBtn = (title) => {
    if (title === "fileRecord") {
      return (
        <Box
          sx={{
            border: `1px solid ${colorPalette.primary}`,
            borderRadius: "5px",
            padding: "0.5rem 1rem",
            color: colorPalette.primary,
            cursor: "pointer",
          }}
          onClick={handleFileRec}
        >
          File Record
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            border: `1px solid ${colorPalette.primary}`,
            borderRadius: "5px",
            padding: "0.5rem 1rem",
            color: colorPalette.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
          onClick={() => setFileUploadModalOpen(true)}
        >
          <UploadIcon />
          Payout Approval
        </Box>
      );
    }
  };

  // Switch Auto Job
  const handleSwitchAutoJobs = async () => {
    try {
      await processSwitchAutoJobs({ status: switchAutoJobStatus });
    } catch (e) {
      console.log(e);
    }
  };

  // Switch Auto Job
  const handleSwitchTitleFetchAutoJobs = async () => {
    try {
      await processSwitchTitleFetchAutoJobs({
        status: switchTitleFetchAutoJobStatus,
      });
    } catch (e) {
      console.log(e);
    }
  };

  // Easypaisa Auto Job
  const handleEasypaisaAutoJobs = async () => {
    try {
      await processEasypaisaAutoJobs({ status: epAutoJobStatus });
    } catch (e) {
      console.log(e);
    }
  };

  // Jazzcash Auto Job
  const handleAutoJobs = async () => {
    try {
      await processAutoJobs({ status: autoJobStatus });
    } catch (e) {
      console.log(e);
    }
  };

  const capitalizeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Switch autojob Title Fetch CTA
  const renderSwitchTitleFetchPayoutJobsBtn = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${colorPalette.primary}`,
          borderRadius: "5px",
          padding: "0.5rem 1rem",
          color: colorPalette.primary,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleSwitchTitleFetchAutoJobs}
      >
        {switchTitleFetchAutoJobStatus === "start" && <PlayCircleOutlineIcon sx={{ marginRight: 1 }} />}
        {switchTitleFetchAutoJobStatus === "stop" && <StopCircleIcon sx={{ marginRight: 1 }} />}
        {capitalizeFirstLetter(switchTitleFetchAutoJobStatus)} Switch Title Fetch
      </Box>
    );
  };

  // Switch autojob CTA
  const renderSwitchPayoutJobsBtn = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${colorPalette.primary}`,
          borderRadius: "5px",
          padding: "0.5rem 1rem",
          color: colorPalette.primary,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleSwitchAutoJobs}
      >
        {switchAutoJobStatus === "start" && <PlayCircleOutlineIcon sx={{ marginRight: 1 }} />}
        {switchAutoJobStatus === "stop" && <StopCircleIcon sx={{ marginRight: 1 }} />}
        {capitalizeFirstLetter(switchAutoJobStatus)} Switch Payments
      </Box>
    );
  };

  // Easypaisa autojob CTA
  const renderEasypaisaPayoutJobsBtn = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${colorPalette.primary}`,
          borderRadius: "5px",
          padding: "0.5rem 1rem",
          color: colorPalette.primary,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleEasypaisaAutoJobs}
      >
        {epAutoJobStatus === "start" && <PlayCircleOutlineIcon sx={{ marginRight: 1 }} />}
        {epAutoJobStatus === "stop" && <StopCircleIcon sx={{ marginRight: 1 }} />}
        {capitalizeFirstLetter(epAutoJobStatus)} Easypaisa Auto Payments
      </Box>
    );
  };

  // Jazzcash auto job CTA
  const renderPayoutJobsBtn = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${colorPalette.primary}`,
          borderRadius: "5px",
          padding: "0.5rem 1rem",
          color: colorPalette.primary,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleAutoJobs}
      >
        {autoJobStatus === "start" && <PlayCircleOutlineIcon sx={{ marginRight: 1 }} />}
        {autoJobStatus === "stop" && <StopCircleIcon sx={{ marginRight: 1 }} />}
        {capitalizeFirstLetter(autoJobStatus)} Jazzcash Auto Payments
      </Box>
    );
  };

  const renderExportBtn = () => {
    return (
      <>
        <CSVLink
          ref={ref}
          data={exportData}
          filename={`Loan_Disbursements_${moment().format("DD-MM-YYYY")}`}
          target="_blank"
          style={{
            display: "none",
          }}
        >
          Download me
        </CSVLink>
        <Button
          onClick={() => {
            const filters = {
              ...search,
              timeZone: momentTimezone.tz.guess(),
            };
            delete (filters as any).page;
            delete (filters as any).limit;
            getLoanRequestsExport(filters).then((response) => {
              setExportData(response?.payload);
            });
          }}
          sx={{
            border: `1px solid ${colorPalette.primary}`,
            borderRadius: "5px",
            padding: "0.5rem 1rem",
            color: colorPalette.primary,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ExportIcon /> Export
        </Button>
      </>
    );
  };

  const renderLoanRequestButton = () => {
    return (
      <Box
        sx={{
          border: `1px solid ${colorPalette.primary}`,
          borderRadius: "5px",
          padding: "0.5rem 1rem",
          color: colorPalette.primary,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => setLoanRequestModal((prev) => "step-1")}
      >
        Loan Request
      </Box>
    );
  };

  const formik = useFormik({
    initialValues: {
      amount: 0,
      cnic: "",
      certName: "",
      payoutType: "",
      bankName: "",
      telco: "",
      msisdn: "",
      bankNo: "",
      billingDate: moment().toDate(), // Default to current date
      channel: "Easypaisa", // Default channel
      source: "disbursal", // Default source
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const response: any = await addAdminLoanRequests({
          amount: values.amount,
          cnic: values.cnic,
          certName: values.certName,
          payoutType: values.payoutType,
          bankName: values.bankName,
          telco: values.telco,
          bankCode: banksList[values.bankName] || "",
          msisdn: values.msisdn,
          bankNo: values.bankNo,
          billingDate: values.billingDate,
          channel: values.channel,
          source: values.source,
        });
        if (!response?.payload?.result?.loanId) {
          setAddLoanRequestResponse({
            status: "failed",
            message:
              response?.payload?.errors?.[0]?.message ??
              response?.error?.message ??
              "Something went wrong. Please try again",
            loanId: null,
          });
        } else {
          setAddLoanRequestResponse({
            status: "success",
            message: "Loan request added successfully with Loan ID: " + response?.payload?.result?.loanId,
            loanId: response?.payload?.result?.loanId,
          });
          resetForm();
        }
      } catch (e) {
        console.error(e);
        setAddLoanRequestResponse({
          status: "failed",
          message: "Failed to add loan request",
          loanId: null,
        });
      }
    },
  });
  const loanRequestModalForm = () => {
    return (
      <Modal
        open={loanRequestModal === "step-1" || loanRequestModal === "step-2" ? true : false}
        onClose={() => {
          setLoanRequestModal(null);
          setAddLoanRequestResponse(null);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{ ...style, maxHeight: "80vh", overflowY: "auto" }}>
          {/*Header*/}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              cursor: "pointer",
              mb: 2,
            }}
            onClick={() => setLoanRequestModal(null)}
          >
            <CloseIcon />
          </Box>
          <Box sx={{ width: "100%" }}>
            <Collapse in={addLoanRequestResponse?.message ? true : false}>
              <Alert
                severity={addLoanRequestResponse?.status === "success" ? "success" : "error"}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setAddLoanRequestResponse(null);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
                sx={{ mb: 2 }}
              >
                {addLoanRequestResponse?.message}
              </Alert>
            </Collapse>
          </Box>
          {/* Use formik to handle the form */}
          <Box component={"form"}>
            <Box display="flex" flexDirection="column" gap={2}>
              {loanRequestModal === "step-1" ? (
                <>
                  <DatePickerComponent
                    label="Billing Date"
                    value={formik?.values?.billingDate}
                    size="medium"
                    onChange={(val) => formik?.setFieldValue("billingDate", val)}
                  />
                  <TextInput
                    type="number"
                    handleTextChange={formik?.handleChange("amount")}
                    placeHolder="Amount"
                    value={formik?.values.amount.toString()}
                    hasError={Boolean(formik?.touched.amount && formik?.errors.amount)}
                    errorMessage={formik?.touched.amount && formik?.errors.amount ? formik?.errors.amount : undefined}
                  />
                  <TextInput
                    handleTextChange={formik?.handleChange("cnic")}
                    placeHolder="CNIC"
                    value={formik?.values.cnic}
                    hasError={Boolean(formik?.touched.cnic && formik?.errors.cnic)}
                    errorMessage={formik?.touched.cnic && formik?.errors.cnic ? formik?.errors.cnic : undefined}
                  />
                  <TextInput
                    handleTextChange={formik?.handleChange("certName")}
                    placeHolder="Full Name"
                    value={formik?.values.certName}
                    hasError={Boolean(formik?.touched.certName && formik?.errors.certName)}
                    errorMessage={
                      formik?.touched.certName && formik?.errors.certName ? formik?.errors.certName : undefined
                    }
                  />
                  <Box>
                    <SelectComponent
                      value={formik?.values.telco}
                      onSelect={(e) => {
                        formik?.setFieldValue("telco", e.target?.value);
                      }}
                      menuItems={[
                        {
                          label: "Jazz",
                          value: "jazz",
                        },
                        {
                          label: "Telenor",
                          value: "telenor",
                        },
                        {
                          label: "Ufone",
                          value: "ufone",
                        },
                        {
                          label: "Zong",
                          value: "zong",
                        },
                      ]}
                      placeHolder="Select Telco"
                    />
                    {formik?.touched.telco && formik?.errors.telco && (
                      <Box sx={{ color: "red", fontSize: 12 }}>{formik?.errors.telco}</Box>
                    )}
                  </Box>
                  <Box>
                    <SelectComponent
                      value={formik?.values.payoutType}
                      onSelect={(e) => formik?.setFieldValue("payoutType", e?.target?.value)}
                      menuItems={[
                        {
                          label: "BANK",
                          value: "BANK",
                        },
                        {
                          label: "WALLET",
                          value: "WALLET",
                        },
                        {
                          label: "CNIC",
                          value: "CNIC",
                        },
                      ]}
                      placeHolder="Select Bank Type"
                    />
                    {formik?.touched.payoutType && formik?.errors.payoutType && (
                      <Box sx={{ color: "red", fontSize: 12 }}>{formik?.errors.payoutType}</Box>
                    )}
                  </Box>
                  <Box>
                    <SelectComponent
                      value={formik?.values.bankName}
                      onSelect={(e) => {
                        formik?.setFieldValue("bankName", e.target?.value);
                      }}
                      menuItems={Object.keys(banksList).map((bank) => ({
                        label: bank,
                        value: bank,
                      }))}
                      placeHolder="Select Bank Name"
                    />
                    {formik?.touched.bankName && formik?.errors.bankName && (
                      <Box sx={{ color: "red", fontSize: 12 }}>{formik?.errors.bankName}</Box>
                    )}
                  </Box>
                  <TextInput
                    handleTextChange={formik?.handleChange("msisdn")}
                    placeHolder="MSISDN"
                    value={formik?.values.msisdn}
                    hasError={Boolean(formik?.touched.msisdn && formik?.errors.msisdn)}
                    errorMessage={formik?.touched.msisdn && formik?.errors.msisdn ? formik?.errors.msisdn : undefined}
                  />
                  <TextInput
                    handleTextChange={formik?.handleChange("bankNo")}
                    placeHolder="Bank Number"
                    value={formik?.values.bankNo}
                    hasError={Boolean(formik?.touched.bankNo && formik?.errors.bankNo)}
                    errorMessage={formik?.touched.bankNo && formik?.errors.bankNo ? formik?.errors.bankNo : undefined}
                  />
                  <Box>
                    <SelectComponent
                      value={formik?.values.channel}
                      onSelect={(e) => {
                        formik?.setFieldValue("channel", e.target?.value);
                      }}
                      menuItems={[
                        {
                          label: "Easypaisa",
                          value: "Easypaisa",
                        },
                        {
                          label: "Jazzcash",
                          value: "Jazzcash",
                        },
                      ]}
                      placeHolder="Select Channel"
                    />
                    {formik?.touched.channel && formik?.errors.channel && (
                      <Box sx={{ color: "red", fontSize: 12 }}>{formik?.errors.channel}</Box>
                    )}
                  </Box>
                  <Box>
                    <SelectComponent
                      value={formik?.values.source}
                      onSelect={(e) => {
                        formik?.setFieldValue("source", e.target?.value);
                      }}
                      menuItems={[
                        {
                          label: "Disbursal",
                          value: "disbursal",
                        },
                        {
                          label: "Referral",
                          value: "referral",
                        },
                        {
                          label: "Insurance",
                          value: "insurance",
                        },
                      ]}
                      placeHolder="Select Source"
                    />
                    {formik?.touched.source && formik?.errors.source && (
                      <Box sx={{ color: "red", fontSize: 12 }}>{formik?.errors.source}</Box>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ fontWeight: 600, mb: 1 }}>Review Information</Box>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box>
                      <strong>Billing Date:</strong>{" "}
                      {formik.values.billingDate ? moment(formik.values.billingDate).format("DD MMM YYYY") : ""}
                    </Box>
                    <Box>
                      <strong>Amount:</strong> {formik.values.amount}
                    </Box>
                    <Box>
                      <strong>CNIC:</strong> {formik.values.cnic}
                    </Box>
                    <Box>
                      <strong>Full Name:</strong> {formik.values.certName}
                    </Box>
                    <Box>
                      <strong>Telco:</strong> {formik.values.telco}
                    </Box>
                    <Box>
                      <strong>Payout Type:</strong> {formik.values.payoutType}
                    </Box>
                    <Box>
                      <strong>Bank Name:</strong> {formik.values.bankName}
                    </Box>
                    <Box>
                      <strong>MSISDN:</strong> {formik.values.msisdn}
                    </Box>
                    <Box>
                      <strong>Bank Number:</strong> {formik.values.bankNo}
                    </Box>
                    <Box>
                      <strong>Source:</strong> {formik.values.source}
                    </Box>
                  </Box>
                </>
              )}
              {addLoanRequestResponse?.loanId ? (
                <SubmitButton
                  title="Close"
                  handlePress={() => {
                    setLoanRequestModal(null);
                    setAddLoanRequestResponse(null);
                  }}
                />
              ) : (
                <SubmitButton
                  title="Submit"
                  handlePress={() => {
                    if (loanRequestModal === "step-1") {
                      setLoanRequestModal("step-2");
                    } else if (loanRequestModal === "step-2") {
                      formik.handleSubmit();
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  };

  const renderCta = () => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            width: "80%",
            justifyContent: "flex-end",
          }}
        >
          {isWalee && (
            <>
              {renderLoanRequestButton()}
              {renderGhostBtn("fileRecord")}
              {renderGhostBtn("loanApproval")}
            </>
          )}
          {canStartStopSwitchJob && renderSwitchPayoutJobsBtn()}
          {canStartStopSwitchJob && renderSwitchTitleFetchPayoutJobsBtn()}
          {canStartStopEpJob && renderEasypaisaPayoutJobsBtn()}
          {canStartStopJcJob && renderPayoutJobsBtn()}
          {renderExportBtn()}
        </Box>
      </>
    );
  };

  const onSearch = async () => {
    try {
      setSearch({ ...search, run: !search.run, page: 0 });
    } catch (e) {
      console.log(e);
    }
  };

  const onPageChange = async (page) => {
    setSearch((prev) => ({
      ...prev,
      page,
      run: !prev.run,
    }));
  };

  const onSortChange = async (obj) => {
    if (obj?.[0]?.field) {
      const { field, sort } = obj[0];
      setSearch({
        ...search,
        sortObj: { field, sortBy: sort },
        page: 0,
        run: !search.run,
      });
    }
  };

  const onReset = () => {
    setSearch((prev) => ({
      query: "",
      status: "All",
      from: new Date(),
      to: new Date(),
      sorting: [],
      filtering: [],
      page: 0,
      limit: 10,
      run: !prev?.run,
      sortObj: {} as any,
      source: "all",
    }));
  };

  const onFilterChange = async (obj) => {
    setSearch({
      ...search,
      filtering: obj.filterModel,
      page: 0,
      run: !search.run,
    });
  };

  useEffect(() => {
    searchLoanRequests(search);
  }, [search.run]);

  useEffect(() => {
    if (exportData?.length && (ref?.current as any)?.link) {
      (ref.current as any).link.click();
    }
  }, [exportData]);

  return (
    <>
      <PageHeader title="Payout Request" renderCta={renderCta} />
      <Box
        sx={{
          marginY: "1rem",
          borderTop: `4px solid ${colorPalette.primary}`,
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
        }}
      >
        <Card>
          <CardContent>
            {/*Search filters*/}
            <Box sx={{ marginY: "1rem" }}>
              <Grid container spacing={2}>
                <Grid item lg={2}>
                  <FormControl fullWidth sx={{ m: 1 }}>
                    {/*<InputLabel htmlFor="outlined-adornment-search">Search</InputLabel>*/}
                    <OutlinedInput
                      id="outlined-adornment-search"
                      value={search.query}
                      size="small"
                      onChange={handleChange("query")}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      placeholder="Search"
                    />
                  </FormControl>
                </Grid>
                <Grid item lg={1.5} marginTop={1}>
                  <FormControl fullWidth>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={search.status}
                      onChange={handleSelectChange}
                      size="small"
                    >
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item lg={1.5} marginTop={1}>
                  <FormControl fullWidth>
                    <Select
                      labelId="source-select-label"
                      id="source-select"
                      value={search.source}
                      onChange={(e) => setSearch({ ...search, source: e.target.value as string })}
                      size="small"
                    >
                      <MenuItem value="all">All Source</MenuItem>
                      <MenuItem value="switch">Switch</MenuItem>
                      <MenuItem value="disbursal">Disbursal</MenuItem>
                      <MenuItem value="referral">Referral</MenuItem>
                      <MenuItem value="null">Null</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item lg={2} marginTop={1}>
                  <DatePickerComponent
                    label="Date From"
                    value={search.from}
                    size="small"
                    onChange={(val) => handleDateChange(val, "from")}
                  />
                </Grid>
                <Grid item lg={2} marginTop={1}>
                  <DatePickerComponent
                    label="Date To"
                    value={search.to}
                    size="small"
                    onChange={(val) => handleDateChange(val, "to")}
                  />
                </Grid>
                <Grid item lg={1} marginTop={1}>
                  <SubmitButton title="Search" handlePress={onSearch} />
                </Grid>
                <Grid item lg={1} marginTop={1}>
                  <OutlinedButton title="Reset" handlePress={onReset} />
                </Grid>
                <Grid item lg={1} marginTop={1}>
                  <SubmitButton title="Summary" handlePress={onReset} />
                </Grid>
              </Grid>
            </Box>
            <div style={{ height: 650, width: "100%" }}>
              <DataGrid
                initialState={{
                  sorting: {
                    sortModel: [{ field: "createdDate", sort: "asc" }],
                  },
                }}
                paginationMode="server"
                filterMode="server"
                disableColumnFilter
                sortingMode="server"
                onSortModelChange={onSortChange}
                onPageChange={onPageChange}
                onFilterModelChange={onFilterChange}
                rows={data?.data || []}
                columns={columns}
                pageSize={search.limit}
                rowsPerPageOptions={[]}
                getRowId={(row) => row._id}
                rowCount={data?.total || 0}
                page={search.page}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                disableSelectionOnClick
              />
            </div>
          </CardContent>
        </Card>
      </Box>

      {/* Loan Request Modal */}

      {loanRequestModal === "step-1" || loanRequestModal === "step-2" ? loanRequestModalForm() : null}

      {/*Modal*/}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/*Header*/}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              cursor: "pointer",
            }}
            onClick={handleClose}
          >
            <CloseIcon />
          </Box>
          <Box
            sx={{
              font: " normal normal 600 18px/27px Poppins",
              marginLeft: "2rem",
            }}
          >
            {loanRec.loanId}
          </Box>
          <Box>
            <Alert
              severity={loanRec.status === "Pending" ? "info" : loanRec.status === "Rejected" ? "error" : "success"}
            >
              {loanRec.status === "Pending" ? "Pending" : loanRec.message}
            </Alert>
          </Box>
          <Grid container spacing={3} marginY={2} marginX={1}>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Name</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <img src={AvatarIcon} alt="avatar" style={{ width: "20px", height: "20px" }} />
                <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.certName}</Box>
              </Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Email</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.beneficiaryEmail}</Box>
            </Grid>
            {/*Phone Number*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Phone No</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.msisdn}</Box>
            </Grid>
            {/*Status*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Status</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{renderStatus(loanRec.status)}</Box>
            </Grid>
            {/*Status Date*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>
                {loanRec.status === "Pending" ? "Requested Date" : "Approval Date"}
              </Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>
                {moment(loanRec.status === "Pending" ? loanRec.createdDate : loanRec.updatedDate).format(
                  "DD MMM YYYY HH:mmA",
                )}
              </Box>
            </Grid>
            {/*Bank Name*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Bank Name</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.bankName}</Box>
            </Grid>
            {/*Bank Name*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Bank Card No</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.bankNo}</Box>
            </Grid>
            {/*Payout Type*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Pay Out Type</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{loanRec.payoutType}</Box>
            </Grid>
            {/*Amount*/}
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 600 14px/21px Poppins" }}>Amount</Box>
            </Grid>
            <Grid item lg={6}>
              <Box sx={{ font: "normal normal 300 14px/21px Poppins" }}>{pkFormatter.format(loanRec.amount)}</Box>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <ConfirmationModal
        open={confirmationModalOpen}
        handleClose={handleCloseConfirmationModal}
        handleExit={handleChangeStatus}
        heading="Are you sure?"
        text="Changing the status will not revert back."
      />
      <LogsModal open={logId} handleClose={handleCloseLogsModal} />
      <UploadModal
        handleClose={handleFileUploadModalClose}
        open={fileUploadModalOpen}
        handleProceed={handleFileUpload}
        onChange={onFileChange}
        file={file}
        value={uploadProgress}
        fileUploadBtnDisabled={fileUploadBtnDisabled}
        fileUploadSummary={fileUploadSummary}
        uploadView={uploadView}
        deleteFile={deleteFile}
        fileParsed={fileParsed}
        error={error}
      />
    </>
  );
};

/**
 * Maps state variables from redux store to props of currect component
 * @param state
 */
const mapStateToProps = (state: RootState) => ({
  state: state.loanRequests,
  token: state.login.token,
  uploadProgress: state.loanRequests.uploadProgress,
});

/**
 * Maps actions from slices to props
 */
const mapDispatchToProps = {
  getLoanRequests: loanRequestsActions.getLoanRequests,
  searchLoanRequests: loanRequestsActions.searchLoanRequests,
  changeStatus: loanRequestsActions.changeStatus,
  fileUpload: loanRequestsActions.fileUpload,
  deleteFile: loanRequestsActions.deleteFile,
  fileParsed: loanRequestsActions.fileParsed,
  resetUploadProgress: loanRequestsActions.resetUploadProgress,
  processAutoJobs: loanRequestsActions.processAutoJobs,
  processEasypaisaAutoJobs: loanRequestsActions.processEasypaisaAutoJobs,
  refreshLoanRequest: loanRequestsActions.refreshLoanRequest,
  deleteLoanRequest: loanRequestsActions.deleteLoanRequest,
  processSwitchAutoJobs: loanRequestsActions.processSwitchAutoJobs,
  processSwitchTitleFetchAutoJobs: loanRequestsActions.processSwitchTitleFetchAutoJobs,
  getJobStatus: loanRequestsActions.getJobStatus,
  getEasypaisaJobStatus: loanRequestsActions.getEasypaisaJobStatus,
  addAdminLoanRequests: loanRequestsActions.addAdminLoanRequests,
  getLoanRequestsExport: loanRequestsActions.getLoanRequestsExport,
};

/**
 * Connects component to redux store
 */
const connector = connect(mapStateToProps, mapDispatchToProps);
const LoanRequestsComponentRedux = connector(LoanRequestsComponent);

export { LoanRequestsComponentRedux as LoanRequestsComponent };
